/**
 * Состояние мит-сессии (DEV-1947).
 *
 * Стор, а не состояние экрана плана: приглашение приходит человеку, где бы он
 * ни был, и баннер о нём висит в общем каркасе. Экран плана и баннер смотрят в
 * одно и то же состояние — иначе «Присоединиться» в шапке и мит-режим внизу
 * расходились бы в том, идёт ли встреча.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import {
  endMeeting,
  getMeetingState,
  inviteToMeeting,
  joinMeeting,
  streamMeeting,
  type MeetingInvite,
  type MeetingSession,
} from './meeting'
import { useNotesStore } from './notesStore'

/**
 * Паузы перед повторной подпиской, миллисекунды.
 *
 * Первая короткая — обрыв чаще всего мгновенный (выкат сервиса, моргнувший
 * wifi), и ждать после него пять секунд значит показывать человеку мёртвый
 * экран на ровном месте. Дальше пауза растёт: если сервис лежит, долбить его
 * каждую секунду с каждой вкладки бессмысленно.
 */
const RECONNECT_DELAYS = [1_000, 2_000, 5_000]

export const useMeetingStore = defineStore('plan-meeting', () => {
  const session = ref<MeetingSession | null>(null)
  const invites = ref<MeetingInvite[]>([])
  const error = ref<unknown>(null)

  /**
   * Человек нажал «Присоединиться» в баннере: плану остаётся открыть мит-режим,
   * когда он до него доедет. Через стор, а не через параметр адреса — режим не
   * состояние страницы, а способ её смотреть.
   */
  const pendingOpen = ref(false)

  /**
   * Связь с встречей потеряна — показываем это, только когда попытка
   * подключиться уже провалилась.
   *
   * Не «поток сейчас не открыт»: между попытками и в первую миллисекунду
   * подключения он тоже не открыт, и красная надпись на ровном месте пугала бы
   * человека там, где всё в порядке.
   */
  const isOffline = ref(false)

  let controller: AbortController | null = null

  const isActive = computed(() => !!session.value)
  const isAuthor = computed(() => session.value?.iAmAuthor ?? false)

  /** Кто на встрече сейчас — позванные, но не пришедшие, в счёт не идут */
  const participants = computed(() =>
    (session.value?.participants ?? []).filter((participant) => participant.joined),
  )

  /** Все, кого касается встреча: сначала пришедшие, следом ещё ожидаемые */
  const everyone = computed(() => [
    ...participants.value,
    ...(session.value?.participants ?? []).filter((participant) => !participant.joined),
  ])

  const refreshState = async () => {
    try {
      const state = await getMeetingState()

      session.value = state.session
      invites.value = state.invites
      error.value = null
    } catch (failure) {
      error.value = failure
    }
  }

  const invite = async (userIds: string[]) => {
    if (!userIds.length) return

    try {
      const state = await inviteToMeeting(userIds)

      session.value = state.session
      invites.value = state.invites
      error.value = null
    } catch (failure) {
      error.value = failure
    }
  }

  const join = async (sessionId: string) => {
    try {
      const state = await joinMeeting(sessionId)

      session.value = state.session
      invites.value = state.invites
      pendingOpen.value = true
      error.value = null
    } catch (failure) {
      error.value = failure
    }
  }

  const end = async () => {
    const current = session.value

    if (!current) return

    try {
      await endMeeting(current.id)
      session.value = null
      error.value = null
    } catch (failure) {
      error.value = failure
    }
  }

  /**
   * Держит поток событий встречи, переподключаясь после обрывов.
   *
   * После каждого переподключения лента и участники перечитываются целиком:
   * события, прошедшие мимо во время обрыва, не восстанавливаются, и без
   * догона экран остался бы со вчерашним протоколом, выглядя живым.
   */
  const openStream = (sessionId: string) => {
    closeStream()

    const notes = useNotesStore()
    const own = new AbortController()

    controller = own

    void (async () => {
      let attempt = 0

      while (!own.signal.aborted) {
        try {
          await streamMeeting(sessionId, {
            signal: own.signal,
            onEvent: (kind) => {
              if (kind === 'note') void notes.load()
              if (kind === 'participants') void refreshState()
              // Встречу завершили — состояние перечитывается, и мит-режим
              // закроется сам: сессии больше нет.
              if (kind === 'ended') void refreshState()
            },
          })

          // Поток закончился без ошибки — так бывает при завершении встречи.
          attempt = 0
          isOffline.value = false
        } catch {
          attempt += 1
          isOffline.value = true
        }

        if (own.signal.aborted) return

        const delay = RECONNECT_DELAYS[Math.min(attempt, RECONNECT_DELAYS.length - 1)] ?? 5_000

        await new Promise((resolve) => setTimeout(resolve, delay))

        if (own.signal.aborted) return

        // Догоняем пропущенное до того, как снова слушать.
        await refreshState()

        if (!session.value) return

        void notes.load()
        isOffline.value = false
      }
    })()
  }

  const closeStream = () => {
    controller?.abort()
    controller = null
    isOffline.value = false
  }

  const consumePendingOpen = () => {
    const wanted = pendingOpen.value
    pendingOpen.value = false

    return wanted
  }

  return {
    session,
    invites,
    error,
    isActive,
    isAuthor,
    isOffline,
    participants,
    everyone,
    pendingOpen,
    refreshState,
    invite,
    join,
    end,
    openStream,
    closeStream,
    consumePendingOpen,
  }
})
